import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { PricingPlans } from "@/components/pricing-plans";

export const FAQS = [
  {
    q: "Why move off Lovable Cloud?",
    a: "Lovable Cloud is perfect for shipping fast. At scale you'll want your own Supabase project — full SQL access, your own dashboard, your own keys, your own backups, no platform lock-in. Most serious products migrate before launch.",
  },
  {
    q: "Will the migration break anything in my Lovable app?",
    a: "Done right, no. The code keeps pointing at Supabase — only the URL and keys change. We migrate the schema, the data, the auth users, the storage, and the edge functions, then flip the env vars. Usually a one-day job for a typical app.",
  },
  {
    q: "Do you keep my data during the migration?",
    a: "We never copy your data to our infrastructure. The migration runs from your Lovable workspace directly into your new Supabase project. We see the schema, not the rows.",
  },
  {
    q: "Can I keep using Lovable after migrating?",
    a: "Yes. That's the whole point. You keep prompting in Lovable; the database, auth and storage just live in your own Supabase from now on.",
  },
];

export const Route = createFileRoute("/services/lovable-cloud-migration")({
  head: () => ({
    meta: [
      { title: "Lovable Cloud to Supabase migration | Vibing Company" },
      {
        name: "description",
        content:
          "Migrate from Lovable Cloud to your own Supabase project. Schema, data, auth, storage, edge functions — done in a day, with zero downtime.",
      },
      { property: "og:title", content: "Lovable Cloud to Supabase migration | Vibing Company" },
      ...ogImageMeta("services-lovable-cloud-migration", "Share card: Lovable Cloud to Supabase migration"),
      {
        property: "og:description",
        content:
          "Move from Lovable Cloud to your own Supabase. Schema, data, auth, storage — done in a day. Zero downtime.",
      },
      { property: "og:url", content: "https://vibing.inc/services/lovable-cloud-migration" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/services/lovable-cloud-migration" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Lovable Cloud to Supabase migration",
          serviceType: "Transfer Lovable Cloud to own Supabase project",
          provider: { "@type": "Organization", name: "the Vibing Company", url: "https://vibing.inc/" },
          areaServed: "Worldwide",
          url: "https://vibing.inc/services/lovable-cloud-migration",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Migration,
});

function Migration() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Why />
      <Day />
      <FAQ />
      <PricingPlans heading="Keep the team after the move" />
      <CTA />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="m-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1 id="m-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02] max-w-[20ch]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        Take back your backend.
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        Transfer your Lovable Cloud project to your own Supabase. Full ownership, full SQL, full backups — without leaving Lovable. Usually a one-day job.
      </p>
      <div className="mt-10 md:mt-14 flex flex-wrap gap-3">
        <TalkToJonButton variant="dark" immersive />
        <MagneticButton href="/services/lovable-development" variant="outline-dark">
          Or hire a Lovable expert
        </MagneticButton>
      </div>
    </section>
  );
}

const WHY = [
  { k: "01", t: "Own your data", b: "Your own dashboard, your own API keys, your own backups. No more 'platform' between you and your rows." },
  { k: "02", t: "Real SQL", b: "Run migrations like a grown-up. Add indexes, write RPCs, set up cron jobs, plug in Postgres extensions." },
  { k: "03", t: "No lock-in", b: "Supabase is open source. If you ever leave Supabase too, you take your Postgres database with you." },
  { k: "04", t: "Keep using Lovable", b: "Lovable still works against your new Supabase. You lose nothing, you gain everything underneath." },
];

function Why() {
  return (
    <section aria-labelledby="why-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="why-h" className="fluid-h2 font-display max-w-[18ch]">Lovable Cloud is great. Yours is better.</h2>
      <ul className="mt-14 grid md:grid-cols-2 gap-5">
        {WHY.map((w) => (
          <li key={w.k} className="bg-background p-8 md:p-12">
            <div className="flex items-baseline gap-5">
              <span className="font-display text-electric leading-none" style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}>{w.k}</span>
              <h3 className="font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{w.t}</h3>
            </div>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[44ch]">{w.b}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

const DAY = [
  { k: "Hour 1", v: "We spin up your Supabase project, replicate the schema, set up row-level security." },
  { k: "Hour 2–4", v: "Migrate data, auth users, storage buckets, and any edge functions you have." },
  { k: "Hour 5", v: "Flip the env vars in Lovable. Smoke-test every flow. Sign-up, login, payments, the lot." },
  { k: "Hour 6", v: "Hand you the keys, the docs, and a rollback plan you'll never need." },
];

function Day() {
  return (
    <section aria-labelledby="d-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 id="d-h" className="fluid-h2 font-display max-w-[14ch]">Six hours, end to end.</h2>
          <p className="mt-6 text-muted-foreground max-w-prose">
            For most apps. Bigger schemas or heavy storage can run to two days — we tell you up front.
          </p>
        </div>
        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {DAY.map((s) => (
            <li key={s.k} className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20">
              <span className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.k}</span>
              <span className="text-base md:text-lg leading-snug">{s.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[20ch]">Lovable Cloud migration, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Send the project.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
            Bring your Lovable URL. Jon will talk through the migration and shape a plan around your project.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block group">
            <span className="mt-3 block font-display text-electric-bright break-all" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
              Talk to Jon<span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
